import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, ChevronRight, ZoomIn, Shield, Package, Phone, FileText, Share2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '5594991858158';

// Usar URL atual do site (dinâmica)
const getSiteUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || 'https://casadasferramentas.com.br';
};

// Ícone WhatsApp
const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={`${className} fill-current`}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export const Produto = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [brand, setBrand] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        // Buscar produto pelo slug ou ID
        const response = await axios.get(`${BACKEND_URL}/api/products/by-slug/${slug}`);
        setProduct(response.data);
        
        // Buscar marca
        if (response.data.brand) {
          const brandsRes = await axios.get(`${BACKEND_URL}/api/brands`);
          const brandData = brandsRes.data.find(b => 
            b.name.toLowerCase() === response.data.brand.toLowerCase()
          );
          setBrand(brandData);
        }

        // Buscar produtos relacionados (mesma categoria)
        const allProducts = await axios.get(`${BACKEND_URL}/api/products`);
        const related = allProducts.data
          .filter(p => p.category === response.data.category && p.id !== response.data.id)
          .slice(0, 4);
        setRelatedProducts(related);

        // Track view
        await axios.post(`${BACKEND_URL}/api/track/view?page=produto&product_id=${response.data.id}&product_name=${encodeURIComponent(response.data.name)}`);
      } catch (error) {
        console.error('Erro ao buscar produto:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProduct();
    window.scrollTo(0, 0);
  }, [slug]);

  const handleWhatsApp = () => {
    const message = encodeURIComponent(`Olá! Tenho interesse no produto: ${product.name}`);
    window.location.href = `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
  };

  const handleShare = async () => {
    const url = `${getSiteUrl()}/produto/${slug}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        window.prompt('Copie o link:', url);
      }
    } else {
      window.prompt('Copie o link:', url);
    }
  };

  const images = product?.images?.length > 0 
    ? product.images 
    : [product?.image || 'https://placehold.co/600x600?text=Produto'];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando produto...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package size={64} className="mx-auto text-gray-400 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Produto não encontrado</h1>
          <p className="text-gray-600 mb-6">O produto que você está procurando não existe ou foi removido.</p>
          <Link to="/catalogo">
            <Button className="bg-blue-900 hover:bg-blue-800">Ver Catálogo</Button>
          </Link>
        </div>
      </div>
    );
  }

  const productUrl = `${getSiteUrl()}/produto/${slug}`;
  const productImage = images[0];

  return (
    <>
      {/* SEO Meta Tags Dinâmicas */}
      <Helmet>
        <title>{product.name} | Casa das Ferramentas - Canaã dos Carajás</title>
        <meta name="description" content={`${product.name} - ${product.description?.slice(0, 150)}. Compre na Casa das Ferramentas em Canaã dos Carajás. ${product.brand} com garantia de fábrica.`} />
        <meta name="keywords" content={`${product.name}, ${product.brand}, ${product.category}, ferramentas, Casa das Ferramentas, Canaã dos Carajás`} />
        <link rel="canonical" href={productUrl} />
        
        {/* Open Graph */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.name} | Casa das Ferramentas`} />
        <meta property="og:description" content={product.description?.slice(0, 200)} />
        <meta property="og:image" content={productImage} />
        <meta property="og:url" content={productUrl} />
        <meta property="product:brand" content={product.brand} />
        <meta property="product:availability" content="in stock" />
        <meta property="product:condition" content="new" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${product.name} | Casa das Ferramentas`} />
        <meta name="twitter:description" content={product.description?.slice(0, 200)} />
        <meta name="twitter:image" content={productImage} />

        {/* Schema.org Product */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": product.name,
            "description": product.description,
            "image": images,
            "brand": {
              "@type": "Brand",
              "name": product.brand
            },
            "category": product.category,
            "offers": {
              "@type": "Offer",
              "availability": "https://schema.org/InStock",
              "seller": {
                "@type": "Organization",
                "name": "Casa das Ferramentas"
              }
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            <Link to="/" className="hover:text-blue-900">Início</Link>
            <span>/</span>
            <Link to="/catalogo" className="hover:text-blue-900">Catálogo</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">{product.name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Galeria de Imagens */}
            <div className="space-y-4">
              <div 
                className="relative bg-white rounded-2xl overflow-hidden shadow-lg aspect-square cursor-zoom-in"
                onClick={() => setIsZoomed(true)}
              >
                <img 
                  src={images[currentImageIndex]} 
                  alt={product.name}
                  className="w-full h-full object-contain p-4"
                />
                {images.length > 1 && (
                  <>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(i => (i - 1 + images.length) % images.length); }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-lg hover:bg-white"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(i => (i + 1) % images.length); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-lg hover:bg-white"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}
                <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  <ZoomIn size={16} /> Clique para ampliar
                </div>
              </div>
              
              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="flex gap-2 justify-center">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentImageIndex(i)}
                      className={`w-16 h-16 rounded-lg overflow-hidden border-2 ${
                        i === currentImageIndex ? 'border-blue-900' : 'border-gray-200'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Informações do Produto */}
            <div className="space-y-6">
              {/* Marca */}
              {brand?.logo ? (
                <div className="bg-white border rounded-lg p-2 inline-block">
                  <img src={brand.logo} alt={product.brand} className="h-8 object-contain" />
                </div>
              ) : (
                <Badge className="bg-blue-900 text-white">{product.brand}</Badge>
              )}

              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">{product.name}</h1>
              
              <p className="text-gray-600 text-lg leading-relaxed">{product.description}</p>

              {/* Specs */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-white rounded-xl border">
                  <Shield className="text-green-600" size={24} />
                  <div>
                    <p className="font-semibold text-gray-900">Garantia</p>
                    <p className="text-sm text-gray-600">De Fábrica</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white rounded-xl border">
                  <Package className="text-blue-600" size={24} />
                  <div>
                    <p className="font-semibold text-gray-900">Original</p>
                    <p className="text-sm text-gray-600">100% Autêntico</p>
                  </div>
                </div>
              </div>

              {/* Ações */}
              <div className="space-y-3 pt-4">
                <Button 
                  onClick={handleWhatsApp}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
                >
                  <WhatsAppIcon className="w-6 h-6 mr-2" />
                  Consultar Preço via WhatsApp
                </Button>
                
                <div className="grid grid-cols-2 gap-3">
                  <Link to={`/orcamento?produto=${product.id}`}>
                    <Button variant="outline" className="w-full border-red-600 text-red-600 hover:bg-red-600 hover:text-white py-5">
                      <FileText size={20} className="mr-2" />
                      Solicitar Orçamento
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    onClick={handleShare}
                    className="w-full py-5"
                  >
                    {copied ? <Check size={20} className="mr-2 text-green-600" /> : <Share2 size={20} className="mr-2" />}
                    {copied ? 'Link Copiado!' : 'Compartilhar'}
                  </Button>
                </div>
              </div>

              {/* Contato Rápido */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <p className="text-blue-900 font-medium flex items-center gap-2">
                  <Phone size={18} />
                  Dúvidas? Fale conosco: (94) 99185-8158
                </p>
              </div>
            </div>
          </div>

          {/* Produtos Relacionados */}
          {relatedProducts.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Produtos Relacionados</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {relatedProducts.map(p => (
                  <Link key={p.id} to={`/produto/${p.slug || p.id}`}>
                    <Card className="hover:shadow-lg transition-shadow h-full">
                      <div className="aspect-square bg-gray-100">
                        <img 
                          src={p.images?.[0] || 'https://placehold.co/300x300?text=Produto'} 
                          alt={p.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <CardContent className="p-3">
                        <Badge className="mb-2 bg-blue-900 text-xs">{p.brand}</Badge>
                        <h3 className="font-semibold text-gray-900 line-clamp-2 text-sm">{p.name}</h3>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Zoom */}
      {isZoomed && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setIsZoomed(false)}
        >
          <img 
            src={images[currentImageIndex]} 
            alt={product.name}
            className="max-w-full max-h-full object-contain"
          />
          <button 
            onClick={() => setIsZoomed(false)}
            className="absolute top-4 right-4 text-white bg-black/50 p-2 rounded-full"
          >
            ✕
          </button>
        </div>
      )}
    </>
  );
};
